import { useQuery } from '@tanstack/react-query';
import { getProdSupabaseClient } from '@vehix/shared/admin/portal-bridge';
import type {
  ActivityFilters,
  ActivitySummaryRow,
  ActivityFeedRow,
  ActivityHeatmapRow,
  TopActiveUserRow,
  ActivityByRoleRow
} from '@vehix/portal/types/userActivity';

// ============================================================================
// Query key factory
// ============================================================================

export const activityKeys = {
  all: ['userActivity'] as const,
  summary: (filters: ActivityFilters) => [...activityKeys.all, 'summary', filters] as const,
  feed: (filters: ActivityFilters, page: number) =>
    [...activityKeys.all, 'feed', filters, page] as const,
  filterOptions: (tenantId?: string) => [...activityKeys.all, 'filterOptions', tenantId] as const,
  heatmap: (filters: ActivityFilters) => [...activityKeys.all, 'heatmap', filters] as const,
  topUsers: (filters: ActivityFilters) => [...activityKeys.all, 'topUsers', filters] as const,
  byRole: (filters: ActivityFilters) => [...activityKeys.all, 'byRole', filters] as const
};

// ============================================================================
// Hooks
// ============================================================================

const FEED_PAGE_SIZE = 20;

/**
 * Fetches aggregated activity summary via the public RPC wrapper.
 */
export function useActivitySummary(filters: ActivityFilters) {
  return useQuery({
    queryKey: activityKeys.summary(filters),
    queryFn: async (): Promise<ActivitySummaryRow[]> => {
      const { data, error } = await getProdSupabaseClient().rpc('get_user_activity_summary', {
        p_tenant_id: filters.tenantId || null,
        p_tenant_role: filters.tenantRole || null,
        p_user_id: filters.userId || null,
        p_date_start: filters.dateStart,
        p_date_end: filters.dateEnd,
        p_activity_category: filters.activityCategory || null,
        p_granularity: filters.granularity
      });

      if (error) throw error;
      return (data as ActivitySummaryRow[]) || [];
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  });
}

/**
 * Fetches paginated activity feed via the public RPC wrapper.
 */
export function useActivityFeed(filters: ActivityFilters, page: number) {
  return useQuery({
    queryKey: activityKeys.feed(filters, page),
    queryFn: async (): Promise<{ rows: ActivityFeedRow[]; totalCount: number }> => {
      const offset = (page - 1) * FEED_PAGE_SIZE;

      const { data, error } = await getProdSupabaseClient().rpc('get_user_activity_feed', {
        p_tenant_id: filters.tenantId || null,
        p_tenant_role: filters.tenantRole || null,
        p_user_id: filters.userId || null,
        p_date_start: filters.dateStart,
        p_date_end: filters.dateEnd,
        p_activity_category: filters.activityCategory || null,
        p_activity_action: filters.activityAction || null,
        p_source_page: filters.sourcePage || null,
        p_limit: FEED_PAGE_SIZE,
        p_offset: offset
      });

      if (error) throw error;

      const rows = (data as ActivityFeedRow[]) || [];
      // Safe: length > 0 check guarantees rows[0] exists
      const totalCount = rows.length > 0 ? Number((rows[0] as ActivityFeedRow).total_count) : 0;

      return { rows, totalCount };
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  });
}

/**
 * Fetches distinct tenant roles and user emails for the filter dropdowns.
 * Queries prod.user_activity_log directly via the prod client.
 */
// eslint-disable-next-line max-lines-per-function
export function useActivityFilterOptions(tenantId?: string) {
  return useQuery({
    queryKey: activityKeys.filterOptions(tenantId),
    queryFn: async (): Promise<{
      roles: string[];
      users: { id: string; email: string }[];
    }> => {
      const prodClient = getProdSupabaseClient();

      // Fetch distinct roles
      let rolesQuery = prodClient
        .from('user_activity_log')
        .select('tenant_role')
        .not('tenant_role', 'is', null);

      if (tenantId) {
        rolesQuery = rolesQuery.eq('tenant_id', tenantId);
      }

      const { data: rolesData } = await rolesQuery;

      const roles = [
        ...new Set((rolesData || []).map((r: { tenant_role: string }) => r.tenant_role))
      ].sort();

      // Fetch distinct users
      let usersQuery = prodClient.from('user_activity_log').select('user_id, user_email');

      if (tenantId) {
        usersQuery = usersQuery.eq('tenant_id', tenantId);
      }

      const { data: usersData } = await usersQuery;

      const userMap = new Map<string, string>();
      (usersData || []).forEach((u: { user_id: string; user_email: string }) => {
        if (u.user_id && u.user_email) {
          userMap.set(u.user_id, u.user_email);
        }
      });

      const users = Array.from(userMap.entries())
        .map(([id, email]) => ({ id, email }))
        .sort((a, b) => a.email.localeCompare(b.email));

      return { roles, users };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

/**
 * Fetches activity heatmap data (hour × day-of-week) via the public RPC wrapper.
 */
export function useActivityHeatmap(filters: ActivityFilters) {
  return useQuery({
    queryKey: activityKeys.heatmap(filters),
    queryFn: async (): Promise<ActivityHeatmapRow[]> => {
      const { data, error } = await getProdSupabaseClient().rpc('get_user_activity_heatmap', {
        p_tenant_id: filters.tenantId || null,
        p_tenant_role: filters.tenantRole || null,
        p_user_id: filters.userId || null,
        p_date_start: filters.dateStart,
        p_date_end: filters.dateEnd,
        p_activity_category: filters.activityCategory || null
      });

      if (error) throw error;
      return (data as ActivityHeatmapRow[]) || [];
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  });
}

/**
 * Fetches top active users ranked by action count via the public RPC wrapper.
 */
export function useTopActiveUsers(filters: ActivityFilters) {
  return useQuery({
    queryKey: activityKeys.topUsers(filters),
    queryFn: async (): Promise<TopActiveUserRow[]> => {
      const { data, error } = await getProdSupabaseClient().rpc('get_top_active_users', {
        p_tenant_id: filters.tenantId || null,
        p_tenant_role: filters.tenantRole || null,
        p_user_id: filters.userId || null,
        p_date_start: filters.dateStart,
        p_date_end: filters.dateEnd,
        p_activity_category: filters.activityCategory || null,
        p_limit: 10
      });

      if (error) throw error;
      return (data as TopActiveUserRow[]) || [];
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  });
}

/**
 * Fetches activity breakdown by tenant role via the public RPC wrapper.
 */
export function useActivityByRole(filters: ActivityFilters) {
  return useQuery({
    queryKey: activityKeys.byRole(filters),
    queryFn: async (): Promise<ActivityByRoleRow[]> => {
      const { data, error } = await getProdSupabaseClient().rpc('get_user_activity_by_role', {
        p_tenant_id: filters.tenantId || null,
        p_tenant_role: filters.tenantRole || null,
        p_user_id: filters.userId || null,
        p_date_start: filters.dateStart,
        p_date_end: filters.dateEnd,
        p_activity_category: filters.activityCategory || null
      });

      if (error) throw error;
      return (data as ActivityByRoleRow[]) || [];
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  });
}

export { FEED_PAGE_SIZE };
